/**
 * Casino Ad Service transport: the real acquisition-campaign backend.
 *
 * Powers the Acquisition campaigns browser (list + detail), the running-campaign
 * artwork/assets, the live workflow/budget status, and the ExoClick performance
 * figures that today are mock or render `?` (see `pages/acquisition/missing-data.md`).
 *
 *   GET /campaigns?tenant_id&status&page_number&page_size  -> paged summaries
 *   GET /campaigns/{id}                                    -> detail + assets
 *   GET /campaigns/{id}/status                             -> workflow/budget/provider
 *   GET /campaigns/{id}/performance?breakdown&date_from&…  -> ExoClick metrics
 *
 * Reached through the owner-hub MLP proxy (`_svc=ad`, cluster scope), so every call
 * carries the brand `tenant_id` and authenticates with the shared raw owner-hub
 * token — see `utils/mlp-proxy.ts` for the URL shape (literal multi-segment path) and the
 * session-safe auth opt-out. A 401/403 from the proxy therefore rejects to the
 * caller (the hooks fall back to `N/A`) without touching the owner-hub session.
 *
 * Full request/response contract: `docs/api.owners-hub.acquisition-campaigns.md`.
 */
import axios from 'axios';

import { createMlpProxyTransport } from '../../../utils/mlp-proxy';

const CAMPAIGNS_PATH = '/campaigns';

/* ── transport ───────────────────────────────────────────────────────────────
 * Casino Ad Service is a CLUSTER service behind the owner-hub MLP proxy (`_svc=ad`):
 * every call carries the brand `tenant_id`, authenticates with the raw owner-hub
 * token, and opts out of the auth interceptor's refresh-and-teardown so a downstream
 * 401/403 can't log the user out. See `utils/mlp-proxy.ts`. */
const { url, requestConfig } = createMlpProxyTransport({
  svc: 'ad',
  scope: 'c',
  headers: { Accept: 'application/json' },
});

/* ── shared shapes ───────────────────────────────────────────────────────────── */

/**
 * Localised campaign name, e.g. `{ en_us: "NeonJack-wb-test" }`. The service keys
 * by locale; resolve a display string with {@link resolveCampaignName}.
 */
export type LocalizedText = Record<string, string>;

/**
 * Provider lifecycle state as the ad-service reports it (title-case). The public
 * vocabulary is closed, but each value is a projection of a wider internal state:
 *
 * | Public     | Internal source                                                  |
 * | ---------- | ---------------------------------------------------------------- |
 * | `Deleted`  | `DELETED`                                                        |
 * | `Failed`   | `FAILED`, or anything ending `_FAILED`                            |
 * | `Starting` | anything not completed / deleted / failed (pipeline in flight)    |
 * | `Running`  | `PROVIDER_PREPARATION_COMPLETED` and not effectively paused       |
 * | `Paused`   | `PROVIDER_PREPARATION_COMPLETED` and effectively paused           |
 *
 * Kept open (`string`) so a value added upstream still parses; map it to the FE
 * `CampaignStatus` with {@link toCampaignStatus}.
 */
export type AdCampaignStatus = 'Starting' | 'Running' | 'Paused' | 'Failed' | 'Deleted' | string;

/** A network/channel the campaign runs on. Observed: `web`. */
export type AdCampaignNetwork = 'web' | string;

/* ── GET /campaigns (list) ─────────────────────────────────────────────────────── */

/** One row of `GET /campaigns`. `start_date` / `end_date` are epoch SECONDS. */
export interface AdCampaignSummary {
  campaign_id: string;
  campaign_name: LocalizedText;
  status: AdCampaignStatus;
  /** Campaign start, epoch SECONDS (×1000 for the FE's epoch-ms fields). */
  start_date: number;
  /** Campaign end, epoch SECONDS, or `null` for an open-ended campaign. */
  end_date: number | null;
  network: AdCampaignNetwork;
  /** CDN URL of the campaign's master key art — the card thumbnail + detail hero image.
   *  Carried on the LIST row itself, so a card needs no extra detail fetch. May be absent. */
  master_key_art?: string;
  /** The campaign's prelander / landing-page path (e.g. `/prelander_ml_test`). */
  landing_page_url?: string;
}

/** `GET /campaigns`: newest-update-first, page-numbered. */
export interface AdCampaignListResponse {
  total_number_of_campaigns: number;
  campaigns: AdCampaignSummary[];
  no_pages: number;
  page_number: number;
}

export interface ListAdCampaignsParams {
  /** Owner tenant id; the brand carries it as a numeric string (`Brand.tenant_id`). */
  tenantId: number | string;
  /** Optional server-side status filter (provider title-case, e.g. `Running`). */
  status?: string;
  /** 1-based page index (default 1). */
  pageNumber?: number;
  /** Page size, 1..100 (default 10). */
  pageSize?: number;
}

/* ── campaign-list request cache ────────────────────────────────────────────────
 * Two sibling hooks list a tenant's campaigns on the same Acquisition load
 * (useAcquisitionCampaigns for the browser pool, useAcquisitionAdSpend for the spend
 * windows). The list is small and rarely changes, so coalesce identical concurrent
 * /near-term calls onto ONE request: cache the in-flight (then resolved) promise by
 * query for a short TTL, so the double-fetch on load becomes a single network call.
 *
 * The shared request deliberately carries NO caller AbortSignal — otherwise one hook
 * unmounting would abort the request the other is still awaiting. The list GET is
 * cheap and side-effect-free; each caller's own `signal` is honoured locally via
 * `withAbort` (the caller's await rejects on abort without cancelling the shared
 * fetch), and every caller already guards on `signal.aborted` before using the
 * result. A failed request is evicted so a transient error isn't served from cache. */
const LIST_CACHE_TTL_MS = 30_000;
const listCache = new Map<string, { at: number; promise: Promise<AdCampaignListResponse> }>();

/** Reject when `signal` aborts, otherwise mirror `promise` — WITHOUT cancelling it
 *  (so a shared cached request survives one caller bailing out). */
const withAbort = <T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> => {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
  return new Promise<T>((resolve, reject) => {
    const onAbort = (): void => reject(new DOMException('Aborted', 'AbortError'));
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      },
    );
  });
};

/** List campaign summaries for a tenant, newest update first. Coalesces identical
 *  concurrent/near-term calls through a short-TTL request cache (see above). */
export const listAdCampaigns = (
  params: ListAdCampaignsParams,
  signal?: AbortSignal,
): Promise<AdCampaignListResponse> => {
  // `tenant_id` rides the proxied URL (see `url()`), not the forwarded query — but it
  // still keys the cache so two brands never share a cached list.
  const query = {
    ...(params.status ? { status: params.status } : {}),
    page_number: params.pageNumber ?? 1,
    page_size: params.pageSize ?? 10,
  };
  const key = JSON.stringify({ tenant_id: params.tenantId, ...query });
  const now = Date.now();
  const cached = listCache.get(key);
  if (cached && now - cached.at < LIST_CACHE_TTL_MS) return withAbort(cached.promise, signal);

  // The shared request runs without the caller signal (see the cache note).
  const promise = axios
    .get<AdCampaignListResponse>(url(CAMPAIGNS_PATH, params.tenantId), requestConfig(undefined, query))
    .then((response) => response.data);
  listCache.set(key, { at: now, promise });
  // Don't cache failures: evict on rejection so the next call retries.
  promise.catch(() => {
    if (listCache.get(key)?.promise === promise) listCache.delete(key);
  });
  return withAbort(promise, signal);
};

/* ── GET /campaigns/{id} (detail + assets) ─────────────────────────────────────── */

/** One generated banner. `name` encodes `"<size> variant <size>|<hex>|<index>"`. */
export interface AdAsset {
  url: string;
  width: number;
  height: number;
  name: string;
}

/** Generated assets keyed by banner size (`"300x250"`, `"728x90"`, …). */
export type AdCampaignAssets = Record<string, AdAsset[]>;

/**
 * The campaign's promotion ("offering") — what the campaign advertises. Present on the
 * DETAIL response only (not the list rows). `promotion_type` / `promotion_cta` are single
 * (unlocalised) strings; the headline `promotion_details.title` is a locale bag (keys like
 * `en` / `en-us` / `es-mx`) — resolve it for the interface language with
 * {@link resolveLocalizedText}.
 */
export interface AdCampaignOffering {
  /** Promotion class, e.g. `Welcome Bonus`. A single (unlocalised) string. */
  promotion_type: string;
  /** Call-to-action label, e.g. `Deposita`. A single (unlocalised) string. */
  promotion_cta: string;
  promotion_details: {
    /** Localised headline, keyed by locale (`en` / `en-us` / `es-mx`, …). */
    title: LocalizedText;
    /** Where the promotion copy was sourced from (provenance; not rendered). */
    source?: Record<string, unknown>;
  };
}

/** `GET /campaigns/{id}`: the list summary plus the generated banner set. */
export interface AdCampaignDetail extends AdCampaignSummary {
  assets: AdCampaignAssets;
  /** The campaign's promotion/offering (detail-only; absent on the list rows). May be absent. */
  offering?: AdCampaignOffering;
}

/** Fetch one campaign's detail (metadata + generated assets, no analytics). */
export const getAdCampaign = async (
  campaignId: string,
  tenantId: number | string,
  signal?: AbortSignal,
): Promise<AdCampaignDetail> => {
  const { data } = await axios.get<AdCampaignDetail>(
    url(`${CAMPAIGNS_PATH}/${campaignId}`, tenantId),
    requestConfig(signal),
  );
  return data;
};

/* ── GET /campaigns/{id}/status (workflow + budget + provider) ─────────────────── */

/** One per-channel/size daily budget reservation. Amounts are EUR CENTS. */
export interface AdBudgetReservation {
  arm_id: string;
  asset_size: string;
  network: AdCampaignNetwork;
  status: string;
  /** `YYYY-MM-DD`, the budget day this reservation belongs to. */
  budget_date: string;
  reserved_eur_cents: number;
  channel_limit_eur_cents: number;
  channel_reserved_eur_cents: number;
}

/** One provider "arm" (a size/network served by a provider campaign). */
export interface AdProviderArm {
  arm_id: string;
  /** Ad network the arm is served through, e.g. `exoclick`. */
  provider: string;
  network: AdCampaignNetwork;
  asset_size: string;
  status: string;
  /** Where the status was read from (e.g. the provider name). */
  status_source: string;
  creative_count: number;
}

/** `GET /campaigns/{id}/status`: lifecycle, provider, and budget state. */
export interface AdCampaignStatusResponse {
  campaign_id: string;
  status: AdCampaignStatus;
  /** Workflow stage, e.g. `provider_preparation`. */
  stage: string;
  /** Stage outcome, e.g. `completed`. */
  stage_status: string;
  /** Internal status enum, e.g. `PROVIDER_PREPARATION_COMPLETED`. */
  internal_status: string;
  workflow_in_progress: boolean;
  /** Step progress; the extra fields appear while a workflow is running. */
  progress: {
    completed: number;
    total: number;
    asset_id?: string;
    size?: string;
    storage_url?: string;
  };
  /** Top-level error, or `null`. */
  error: string | null;
  /** Per-stage errors (each `null` when the stage is clean). */
  errors: Record<string, string | null>;
  budget: {
    status: string;
    reservations: AdBudgetReservation[];
  };
  provider: {
    status: string;
    refresh_errors: unknown[];
    arms: AdProviderArm[];
  };
}

/** Fetch a campaign's workflow / provider / budget status. */
export const getAdCampaignStatus = async (
  campaignId: string,
  tenantId: number | string,
  signal?: AbortSignal,
): Promise<AdCampaignStatusResponse> => {
  const { data } = await axios.get<AdCampaignStatusResponse>(
    url(`${CAMPAIGNS_PATH}/${campaignId}/status`, tenantId),
    requestConfig(signal),
  );
  return data;
};

/* ── GET /campaigns/{id}/performance (ExoClick metrics) ────────────────────────── */

/** Metric breakdown. `summary` returns totals + `top_sites`; the rest return `rows`. */
export type AdPerformanceBreakdown = 'summary' | 'campaign' | 'variation' | 'site' | 'category';

/** The metric block shared by totals, top-sites, and breakdown rows. */
export interface AdPerformanceMetrics {
  impressions: number;
  clicks: number;
  /** Spend in the response `currency` (decimal units, e.g. dollars). */
  cost: number;
  /** Click-through rate, fraction (0..1). */
  ctr: number;
  /** Cost per click. */
  cpc: number;
  /** Cost per mille (per 1000 impressions). */
  cpm: number;
}

/** A `top_sites` entry (and a `breakdown=site` row): metrics keyed by publisher. */
export interface AdPerformanceSite extends AdPerformanceMetrics {
  site: string;
}

/**
 * A non-summary breakdown row. Carries the metric block plus the breakdown's
 * dimension key (`site` / `variation` / `category` / …). The exact non-`site`
 * dimension keys are unconfirmed (test rows are empty), so they are left open.
 */
export type AdPerformanceRow = AdPerformanceMetrics & Record<string, unknown>;

/** One provider campaign rolled into the figures (and its raw row count). */
export interface AdPerformanceProviderCampaign {
  arm_id: string;
  asset_size: string;
  network: AdCampaignNetwork;
  exoclick_campaign_id: number;
  row_count: number;
}

/** `GET /campaigns/{id}/performance`: sanitized ExoClick metrics. */
export interface AdPerformanceResponse {
  campaign_id: string;
  campaign_name: LocalizedText;
  provider: string;
  breakdown: AdPerformanceBreakdown;
  /** The echoed range. `null` when the request omitted `date_from` / `date_to`. */
  date_from: string | null;
  date_to: string | null;
  /** Money currency for `cost` / `cpc` / `cpm`, e.g. `USD`. */
  currency: string;
  totals: AdPerformanceMetrics;
  provider_campaigns: AdPerformanceProviderCampaign[];
  /** Present for `breakdown=summary` (the top publishers by the metrics). */
  top_sites?: AdPerformanceSite[];
  /** Present for the non-summary breakdowns (site / variation / category / campaign). */
  rows?: AdPerformanceRow[];
}

export interface AdPerformanceParams {
  /** Default `summary`. */
  breakdown?: AdPerformanceBreakdown;
  /** `YYYY-MM-DD`. */
  dateFrom?: string;
  /** `YYYY-MM-DD`. */
  dateTo?: string;
  /** Max provider rows fetched per page, 1..5000 (default 500). */
  pageLimit?: number;
  /** Max aggregated rows returned, 1..100 (default 10). */
  rowLimit?: number;
}

/** Fetch a campaign's performance figures for a date range and breakdown. */
export const getAdCampaignPerformance = async (
  campaignId: string,
  tenantId: number | string,
  params: AdPerformanceParams = {},
  signal?: AbortSignal,
): Promise<AdPerformanceResponse> => {
  const { data } = await axios.get<AdPerformanceResponse>(
    url(`${CAMPAIGNS_PATH}/${campaignId}/performance`, tenantId),
    requestConfig(signal, {
      breakdown: params.breakdown ?? 'summary',
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
      ...(params.pageLimit != null ? { page_limit: params.pageLimit } : {}),
      ...(params.rowLimit != null ? { row_limit: params.rowLimit } : {}),
    }),
  );
  return data;
};

/* ── mapping helpers (ad-service shape -> the FE's mock-data types) ─────────────── */

/** Resolve a localised name to a display string (prefers `lang`, then `en_us`). */
export const resolveCampaignName = (name: LocalizedText, lang = 'en'): string => {
  const exact = name[lang] ?? name[`${lang}_us`] ?? name.en_us;
  if (exact) return exact;
  const first = Object.values(name)[0];
  return first ?? '';
};

/**
 * Resolve a localised text bag to a display string for the interface language `lang`
 * (the app's short code, e.g. `en` / `mx`). Separator- and case-insensitive, and aware
 * of the app's region-as-language convention: it matches `mx` against a full `es-mx`
 * key (region match), then a language prefix (`en` -> `en-us`), then falls back to
 * English, then the first available value. Returns '' for an empty/absent bag.
 *
 * Unlike {@link resolveCampaignName} (the underscore-keyed `campaign_name` bag, a single
 * brand string), this handles the hyphen-keyed, multi-locale bags the service uses for
 * translated copy such as the offering headline (`offering.promotion_details.title`).
 */
export const resolveLocalizedText = (bag: LocalizedText | undefined, lang = 'en'): string => {
  if (!bag) return '';
  const keys = Object.keys(bag);
  if (keys.length === 0) return '';
  const norm = (value: string): string => value.toLowerCase().replace(/_/g, '-');
  const want = norm(lang);
  const parts = (key: string): string[] => norm(key).split('-');
  const pick =
    keys.find((key) => norm(key) === want) ?? // exact: en -> en, es-mx -> es-mx
    keys.find((key) => parts(key)[1] === want) ?? // region: mx -> es-mx
    keys.find((key) => parts(key)[0] === want) ?? // prefix: en -> en-us
    keys.find((key) => parts(key)[0] === 'en') ?? // English fallback
    keys[0];
  return bag[pick] ?? '';
};

/**
 * Map the provider lifecycle status onto the FE `CampaignStatus` union
 * (`mock-data/types.ts`), or `null` when the studio does not show that campaign:
 *
 *   `Running`  → `running`
 *   `Paused`   → `paused`
 *   `Starting` → null          still being built, nothing to show yet
 *   `Failed`   → null          never shown
 *   `Deleted`  → null          never shown
 *   anything else → null       an unrecognised state is dropped, not guessed at
 *
 * A `null` means callers DROP the campaign rather than dress a dead one up as live
 * (this used to fall through to `running`, which put a Failed campaign in the
 * Running tab under a green LIVE dot).
 *
 * **`Starting` is deliberately not a `suggestion`.** It reads like one (the owner
 * has yet to see it live), and mapping it that way put real, in-flight campaigns on
 * the Suggested tab — whose detail page is still fed by `mock-data/suggested-campaign`
 * and describes a Halloween promo, an offering and a channel list that belong to no
 * real campaign. This is a customer-facing surface, so it shows nothing rather than
 * invented specifics: an in-flight campaign is simply absent until the provider
 * finishes building it, and the Suggested tab stays genuinely empty until a real
 * suggestions source is wired.
 *
 * `ended` never comes from this field: the provider has no such state, so it is
 * derived from `end_date` having passed (see `utils/campaign-status`) — which is
 * what turns a Paused campaign with a spent deadline into an ended one.
 */
export const toCampaignStatus = (status: AdCampaignStatus): 'running' | 'paused' | null => {
  const normalized = typeof status === 'string' ? status.toLowerCase() : '';
  if (normalized === 'running') return 'running';
  if (normalized === 'paused') return 'paused';
  return null;
};
